import { Spin } from "antd";
import { DefaultButton } from "components/Shared/DefaultButton";
import { useDeleteEntityImage, useUploadEntityImage } from "hooks/useEntityImageApi";
import { useCallback, useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { useIntl } from "react-intl";

type Props = {
  entity: any;
  imageType: string;
  readonly: boolean;
};

const allowedFileTypes = { "image/jpeg": [".jpg"], "image/png": [".png"] };

export const ImageUploadForm = ({ entity, imageType, readonly }: Props) => {
  const { formatMessage } = useIntl();
  const [base64file, setBase64File] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>();
  const { isPending: isUploading, mutateAsync: uploadImage } = useUploadEntityImage();
  const { isPending: isDeleting, mutateAsync: deleteImage, isSuccess: isSuccessfullyDeleted } = useDeleteEntityImage();
  const image = entity.images?.filter((img) => img?.imageType === imageType.toUpperCase())?.[0];

  useEffect(() => {
    if (isSuccessfullyDeleted) {
      setBase64File(null);
    }
  }, [isSuccessfullyDeleted]);

  useEffect(() => {
    if (!file && image) {
      setBase64File(`${image?.url}?size=400`);
    }
  }, [image, file]);

  const onDropHandler = useCallback((files: Array<File | null>) => {
    const firstFile = files?.[0];
    if (!firstFile) {
      return;
    }

    if (!Object.keys(allowedFileTypes).includes(firstFile.type)) {
      console.warn("The file type is not supported.", {
        fileType: firstFile.type,
      });
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(firstFile);
    reader.onload = () => {
      setBase64File(reader?.result as string);
      setFile(firstFile);
    };
    reader.onerror = (error) => {
      console.error("Error: ", error);
    };
  }, []);

  const onUploadButtonClicked = async () => {
    if (file) {
      await uploadImage({ entityId: entity.entityId, data: file, imageType });
      setFile(null);
    }
  };

  const onClearButtonClicked = async () => {
    await deleteImage({ entityId: entity.entityId, imageId: image?.imageId });
  };

  return (
    <div
      className="upload"
      style={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "#ffffff",
        padding: "10px",
        flexDirection: "column",
        height: "max-content",
      }}
    >
      <div
        style={{
          width: "290px",
          padding: "5px",
          minHeight: "300px",
        }}
      >
        {isUploading || isDeleting ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "inherit",
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <div
            style={{
              top: 0,
              width: "100%",
              height: "100%",
            }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                backgroundColor: "#F3F3F3",
                height: "290px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {base64file || image?.url ? (
                <img
                  style={{
                    height: "fit-content",
                    width: "100%",
                    margin: 0,
                  }}
                  alt={imageType}
                  src={base64file || `${image.url}?size=400`}
                />
              ) : (
                <svg
                  aria-hidden="true"
                  focusable="false"
                  style={{
                    height: "100%",
                  }}
                >
                  <line
                    style={{
                      stroke: "#707070",
                      strokeWidth: 1,
                    }}
                    x1="0"
                    y1="100%"
                    x2="100%"
                    y2="0"
                  ></line>
                  <line
                    style={{
                      stroke: "#707070",
                      strokeWidth: 1,
                    }}
                    x1="0"
                    y1="0"
                    x2="100%"
                    y2="100%"
                  ></line>
                </svg>
              )}
            </div>
            {!readonly && (
              <Dropzone onDrop={onDropHandler} accept={allowedFileTypes}>
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <section className={`dropZone dragActive-${isDragActive}`}>
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <p
                        style={{
                          border: "1px dashed #ccc",
                          padding: "15px 10px",
                          lineHeight: "1.1rem",
                          textAlign: "center",
                          display: "block",
                          background: "#eceff1",
                          marginTop: "5px",
                        }}
                      >
                        {formatMessage({ id: "entityEditPage.image.dragzone" })}
                      </p>
                    </div>
                  </section>
                )}
              </Dropzone>
            )}
          </div>
        )}
      </div>
      {!readonly && (
        <div
          style={{
            marginTop: "5px",
            display: "flex",
            gap: "5px",
            justifyContent: "center",
          }}
        >
          <DefaultButton disabled={!file} loading={isUploading} onClick={onUploadButtonClicked}>
            {formatMessage({ id: "button.upload" })}
          </DefaultButton>
          <DefaultButton loading={isDeleting} danger={true} disabled={!image} onClick={onClearButtonClicked}>
            {formatMessage({ id: "button.clear" })}
          </DefaultButton>
        </div>
      )}
    </div>
  );
};
